function slugify(input) {
  return String(input || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
}

export function normalizeTeamName(row = {}) {
  return (
    row.category ||
    row.team_name ||
    row.team ||
    row.team_display_name ||
    row.name ||
    row.school ||
    row.club ||
    ''
  )
}

export function normalizeLeague(row = {}) {
  const rawLeague = row.league || row.sport || row.league_name || row.competition || row.sport_key || row.sport_title || ''
  const normalized = String(rawLeague).trim()
  if (!normalized) return ''

  const key = normalized.toUpperCase().replace(/[^A-Z0-9]/g, '')
  if (key.includes('AMERICANFOOTBALLNFL') || key === 'NFL') return 'NFL'
  if (key.includes('BASEBALLMLB') || key === 'MLB') return 'MLB'
  if (key.includes('BASKETBALLNBA') || key === 'NBA') return 'NBA'
  if (key.includes('BASKETBALLWNBA') || key === 'WNBA') return 'WNBA'
  if (key.includes('ICEHOCKEYNHL') || key === 'NHL') return 'NHL'
  if (key.includes('NCAAF')) return 'NCAAF'
  if (key.includes('NCAAB') || key.includes('NCAAM')) return 'NCAAB'
  if (key.includes('NCAAW')) return 'NCAAW'

  return normalized
}

export function normalizeMarket(row = {}) {
  return row.market || row.bet_type || row.wager_type || ''
}

export function buildTeamKey(row = {}) {
  const team = normalizeTeamName(row)
  const league = normalizeLeague(row)
  return `${team}__${league}`
}

export function buildTrendEntityKey(row = {}) {
  const category = normalizeTeamName(row)
  const league = normalizeLeague(row)
  const market = normalizeMarket(row)
  return `${category}__${league}__${market}`
}

export function createTeamSlugMap(rows = []) {
  const byTeam = new Map()

  rows.forEach((row) => {
    const teamName = normalizeTeamName(row)
    if (!teamName) return

    const league = normalizeLeague(row)
    const key = `${teamName}__${league}`

    if (!byTeam.has(key)) {
      byTeam.set(key, { teamName, league })
    }
  })

  const sorted = Array.from(byTeam.values()).sort((a, b) => {
    const teamSort = a.teamName.localeCompare(b.teamName)
    if (teamSort !== 0) return teamSort
    return a.league.localeCompare(b.league)
  })

  const taken = new Set()
  const slugToTeam = new Map()
  const teamToSlug = new Map()

  sorted.forEach((entry) => {
    const teamBase = slugify(entry.teamName)
    const leagueBase = slugify(entry.league)
    let slug = teamBase || 'team'

    if (taken.has(slug) && leagueBase) {
      slug = `${teamBase}-${leagueBase}`
    }

    let index = 2
    let uniqueSlug = slug

    while (taken.has(uniqueSlug)) {
      uniqueSlug = `${slug}-${index}`
      index += 1
    }

    taken.add(uniqueSlug)

    const teamKey = `${entry.teamName}__${entry.league}`
    slugToTeam.set(uniqueSlug, entry)
    teamToSlug.set(teamKey, uniqueSlug)
  })

  return { slugToTeam, teamToSlug }
}

export function createTrendEntitySlugMap(rows = []) {
  const byEntity = new Map()

  rows.forEach((row) => {
    const category = normalizeTeamName(row)
    if (!category) return

    const league = normalizeLeague(row)
    const market = normalizeMarket(row)
    const key = `${category}__${league}__${market}`

    if (!byEntity.has(key)) {
      byEntity.set(key, { category, league, market })
    }
  })

  const sorted = Array.from(byEntity.values()).sort((a, b) => {
    const categorySort = a.category.localeCompare(b.category)
    if (categorySort !== 0) return categorySort
    const leagueSort = a.league.localeCompare(b.league)
    if (leagueSort !== 0) return leagueSort
    return a.market.localeCompare(b.market)
  })

  const taken = new Set()
  const slugToEntity = new Map()
  const entityToSlug = new Map()

  sorted.forEach((entry) => {
    const categoryBase = slugify(entry.category)
    const leagueBase = slugify(entry.league)
    const marketBase = slugify(entry.market)

    let slug = categoryBase || 'entity'
    if (taken.has(slug) && leagueBase) slug = `${slug}-${leagueBase}`
    if (taken.has(slug) && marketBase) slug = `${slug}-${marketBase}`

    let index = 2
    let uniqueSlug = slug
    while (taken.has(uniqueSlug)) {
      uniqueSlug = `${slug}-${index}`
      index += 1
    }

    taken.add(uniqueSlug)
    const key = `${entry.category}__${entry.league}__${entry.market}`
    slugToEntity.set(uniqueSlug, entry)
    entityToSlug.set(key, uniqueSlug)
  })

  return { slugToEntity, entityToSlug }
}
